// src/feature-module/subscription/SubscriptionRenewal.jsx
import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Switch,
  Alert,
  Typography,
  Skeleton,
  Modal,
  message,
  Result,
  Divider,
  Empty,
  Segmented,
  Input,
  Form,
  InputNumber,
  Grid,
  Collapse,
} from "antd";
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarCircleOutlined,
  SyncOutlined,
  StopOutlined,
  CrownOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import {
  getLatestFeatureSubscription,
  previewNextFeatureRenewal,
  setFeatureAutoRenew,
  cancelFeatureSubscription,
  startFeatureSubscriptionWithProvider,
  listPosInventoryPackages,
} from "../../api/subscriptionApi";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const t = (s) =>
  typeof window !== "undefined" && typeof window.translate === "function"
    ? window.translate(s)
    : s;

const getPermissionDeniedText = () => {
  const lang = (localStorage.getItem("lang") || "en").toLowerCase();
  return lang.startsWith("ar")
    ? "ليس لديك صلاحية للوصول إلى اشتراك نقاط البيع. يرجى التواصل مع المدير."
    : "You don't have permission to access POS Subscription. Please contact your admin.";
};

const isPermissionDeniedError = (e) => {
  const status = e?.response?.status;
  if (!(status === 401 || status === 403)) return false;

  const raw = e?.response?.data || {};
  const msg = String(raw?.message || raw?.error || "").toLowerCase();
  return (
    status === 403 ||
    /permission|forbidden|access\s*denied|not\s*allowed|insufficient|unauthorized/.test(
      msg
    ) ||
    /صلاحية|غير\s*مسموح/.test(msg)
  );
};

const showPermissionDeniedToast = (e) => {
  if (e?.__permissionToastShown) return;
  message.error({
    content: getPermissionDeniedText(),
    key: "pos-subscription-permission-denied",
    duration: 4,
  });
};

function money(v, ccy = "KWD") {
  if (v == null) return "—";
  const n = Number(v);
  return `${ccy} ${n.toLocaleString()}`;
}

function parsePairId(id = "") {
  const [providerPackageId, providerPackageOptionId] = String(id).split(":");
  return { providerPackageId, providerPackageOptionId };
}

// safe price getter (supports both old shape {discounted} and new shape {price:{yearly,monthly}})
function getRowPrice(row, billing) {
  if (!row) return 0;
  if (row.price && typeof row.price === "object") {
    const v = row.price[billing];
    return Number(v || 0);
  }
  // fallback to your old field name
  return Number(row.discounted || 0);
}

function normalizeName(s = "") {
  return String(s).trim().toLowerCase();
}

function LimitPill({ label, value }) {
  if (value == null) return null;
  return (
    <Tag style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12 }}>
      {t(label)}: <b>{value}</b>
    </Tag>
  );
}
LimitPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function Hero({ sub, preview, active, daysLeft, onOpenPlans }) {
  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 18,
        background:
          "linear-gradient(135deg, #1677FF 0%, #4C8DFF 45%, #88A8FF 100%)",
        boxShadow: "0 10px 30px rgba(22,119,255,0.20)",
        color: "#fff",
      }}
      bodyStyle={{ padding: 18 }}
    >
      <Row gutter={[14, 14]} align="middle">
        <Col xs={24} md={14}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Space>
              <CrownOutlined />
              <Title level={4} style={{ margin: 0, color: "#fff" }}>
                {sub?.packageName || "—"}
              </Title>
              <Tag color={active ? "green" : "red"} icon={<CheckCircleOutlined />}>
                {active ? t("Active") : t("Inactive")}
              </Tag>
              {Number.isFinite(daysLeft) && (
                <Tag color="gold">{daysLeft} {t("days left")}</Tag>
              )}
            </Space>

            <Text style={{ color: "rgba(255,255,255,0.88)" }}>
              {(sub?.interval || "yearly").toUpperCase()} · {t("Next billing")}{" "}
              {preview?.nextBillingAt
                ? dayjs(preview.nextBillingAt).format("DD MMM YYYY")
                : "—"}
            </Text>

            <Space wrap>
              <LimitPill label="Products" value={sub?.packageOption?.limits?.productLimit} />
              <LimitPill label="Users" value={sub?.packageOption?.limits?.userAccessLimit} />
              <LimitPill label="Stores" value={sub?.packageOption?.limits?.storeLimit} />
              <LimitPill label="Warehouses" value={sub?.packageOption?.limits?.warehouseLimit} />
            </Space>
          </Space>
        </Col>

        <Col xs={24} md={10} style={{ textAlign: "right" }}>
          <Space direction="vertical" align="end" style={{ width: "100%" }}>
            <Text style={{ color: "rgba(255,255,255,0.85)" }}>
              {t("Billed amount")}
            </Text>
            <Title level={2} style={{ margin: 0, color: "#fff" }}>
              {money(preview?.amount, preview?.currency || sub?.currency)}
            </Title>
            <Button
              size="large"
              type="primary"
              onClick={onOpenPlans}
              style={{
                borderRadius: 12,
                background: "rgba(255,255,255,0.18)",
                borderColor: "rgba(255,255,255,0.25)",
              }}
            >
              {t("Change Plan")}
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}

Hero.propTypes = {
  sub: PropTypes.object,
  preview: PropTypes.object,
  active: PropTypes.bool,
  daysLeft: PropTypes.number,
  onOpenPlans: PropTypes.func.isRequired,
};

function PlanCard({ row, billing, selecting, onChoose, currency }) {
  const pkg = row.pkg;
  const opt = row.opt;
  const price = getRowPrice(row, billing);

  return (
    <Card
      hoverable
      style={{
        borderRadius: 16,
        height: "100%",
        border: "1px solid rgba(0,0,0,0.06)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div>
            <Text strong style={{ fontSize: 15 }}>{pkg?.name_en}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {opt?.name_en || "Base"}
              </Text>
            </div>
          </div>

          <Tag
            color="blue"
            style={{
              fontSize: 13,
              padding: "6px 10px",
              borderRadius: 999,
              height: "fit-content",
            }}
          >
            {money(price, currency)}
          </Tag>
        </div>

        <Text type="secondary" style={{ fontSize: 13, minHeight: 34 }}>
          {opt?.value_en || "—"}
        </Text>

        <Divider style={{ margin: "6px 0" }} />

        <Button
          type="primary"
          block
          size="large"
          loading={selecting}
          style={{ borderRadius: 12 }}
          onClick={() =>
            onChoose({
              providerPackageId: pkg.id,
              providerPackageOptionId: opt?.id,
            })
          }
        >
          {t("Choose")}
        </Button>

        <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
          {billing === "monthly" ? t("Billed monthly") : t("Billed yearly")}
        </Text>
      </Space>
    </Card>
  );
}

PlanCard.propTypes = {
  row: PropTypes.object.isRequired,
  billing: PropTypes.oneOf(["monthly", "yearly"]).isRequired,
  selecting: PropTypes.bool,
  onChoose: PropTypes.func.isRequired,
  currency: PropTypes.string,
};

export default function SubscriptionRenewal() {
  const screens = useBreakpoint();

  const pageStyle = React.useMemo(
    () => ({
      maxWidth: 1200,
      margin: "0 auto",
      padding: screens.xs ? 12 : 16,
    }),
    [screens.xs]
  );

  const [creds, setCreds] = React.useState({ storeId: null, email: "", password: "" });

  const [loading, setLoading] = React.useState(false);
  const [sub, setSub] = React.useState(null);
  const [active, setActive] = React.useState(false);
  const [daysLeft, setDaysLeft] = React.useState(null);
  const [preview, setPreview] = React.useState(null);

  const [autoRenew, setAutoRenew] = React.useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = React.useState(false);

  const [suggestions, setSuggestions] = React.useState([]);

  const [packagesLoading, setPackagesLoading] = React.useState(false);
  const [pkgRows, setPkgRows] = React.useState([]);
  const [showPlans, setShowPlans] = React.useState(false);
  const [selecting, setSelecting] = React.useState(false);

  const [billingView, setBillingView] = React.useState("yearly"); // monthly/yearly
  const [planFilter, setPlanFilter] = React.useState("All");
  const [search, setSearch] = React.useState("");

  const [form] = Form.useForm();

  const features = React.useMemo(() => ["website", "pos", "inventory"], []);
  const canRun = React.useMemo(
    () => Boolean(creds.storeId) && Boolean(creds.email),
    [creds.storeId, creds.email]
  );

  const load = React.useCallback(
    async (params) => {
      const storeId = params?.storeId ?? creds.storeId;
      const email = params?.email ?? creds.email;
      const localCanRun = Boolean(storeId) && Boolean(email);
      if (!localCanRun) return;

      setLoading(true);
      try {
        const latest = await getLatestFeatureSubscription({ storeId, email, features });
        setSub(latest.subscription);
        setActive(Boolean(latest.isActive));
        setDaysLeft(latest.daysLeft);
        setSuggestions(latest.suggestions || []);
        setAutoRenew(Boolean(latest.subscription?.autoRenew));
      } catch (e) {
        if (isPermissionDeniedError(e)) {
          showPermissionDeniedToast(e);
          setSub(null);
          setLoading(false);
          return;
        }
        const status = e?.response?.status;
        message.warning(
          status === 404
            ? t("No active subscription.")
            : e?.response?.data?.error || e.message || t("Failed to load subscription")
        );
        setSub(null);
        setLoading(false);
        return;
      }

      try {
        const prev = await previewNextFeatureRenewal({ storeId, email, features });
        setPreview(prev);
      } catch (e) {
        setPreview(null);
      } finally {
        setLoading(false);
      }
    },
    [creds.storeId, creds.email, features]
  );

  const onSubmitCreds = (vals) => {
    const next = {
      storeId: vals.storeId,
      email: vals.email?.trim(),
      password: vals.password || "",
    };
    setCreds(next);
    load(next);
  };

  async function onToggleAutoRenew(checked) {
    if (!sub) return;
    try {
      setAutoRenewSaving(true);
      setAutoRenew(checked);

      await setFeatureAutoRenew({
        storeId: creds.storeId,
        email: creds.email,
        features,
        enable: checked,
        password: creds.password || "",
      });

      setSub((prev) => (prev ? { ...prev, autoRenew: checked } : prev));
      message.success(checked ? t("Auto-renew enabled") : t("Auto-renew disabled"));
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      setAutoRenew((prev) => !prev);
      message.error(e?.response?.data?.error || t("Failed to update auto-renew"));
    } finally {
      setAutoRenewSaving(false);
    }
  }

  async function onCancel() {
    Modal.confirm({
      title: t("Cancel subscription?"),
      icon: <ExclamationCircleOutlined />,
      content: t("Access stays active until the current period ends."),
      okText: t("Cancel Subscription"),
      okType: "danger",
      onOk: async () => {
        try {
          await cancelFeatureSubscription({
            storeId: creds.storeId,
            email: creds.email,
            features,
          });
          message.success(t("Subscription will not renew."));
          await load();
        } catch (e) {
          if (isPermissionDeniedError(e)) {
            showPermissionDeniedToast(e);
            return;
          }
          message.error(e?.response?.data?.error || t("Cancel failed"));
        }
      },
    });
  }

  async function beginCheckout({ providerPackageId, providerPackageOptionId }) {
    try {
      setSelecting(true);
      const r = await startFeatureSubscriptionWithProvider({
        storeId: creds.storeId,
        email: creds.email,
        providerPackageId,
        providerPackageOptionId,
        features,
        autoRenew: true,
      });
      if (r?.url) window.location.href = r.url;
      else message.error(t("Failed to start checkout"));
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      message.error(e?.response?.data?.error || e.message || t("Checkout failed"));
    } finally {
      setSelecting(false);
    }
  }

  async function openPlans() {
    setShowPlans(true);
    if (pkgRows.length) return;

    try {
      setPackagesLoading(true);
      const rows = await listPosInventoryPackages();

      // ✅ show ONLY current package (by name fallback)
      const currentName = normalizeName(sub?.packageName || "");
      const filtered = Array.isArray(rows)
        ? rows.filter((r) => normalizeName(r?.pkg?.name_en || "") === currentName)
        : [];

      setPkgRows(filtered);
    } catch (e) {
      if (isPermissionDeniedError(e)) {
        showPermissionDeniedToast(e);
        return;
      }
      message.error(t("Failed to load packages"));
    } finally {
      setPackagesLoading(false);
    }
  }

  async function reloadPlans() {
    setPkgRows([]);
    await openPlans();
  }

  const filteredRows = React.useMemo(() => {
    let rows = pkgRows.slice();

    if (planFilter !== "All") {
      rows = rows.filter((r) =>
        (r.opt?.name_en || "Base").toLowerCase().includes(planFilter.toLowerCase())
      );
    }

    if (search.trim()) {
      rows = rows.filter((r) => r.searchText.includes(search.trim().toLowerCase()));
    }

    rows.sort((a, b) => getRowPrice(a, billingView) - getRowPrice(b, billingView));
    return rows;
  }, [pkgRows, planFilter, search, billingView]);

  const currency = preview?.currency || sub?.currency || "KWD";

  return (
    <div style={pageStyle}>
      {/* Compact top bar */}
      <Card style={{ borderRadius: 14, marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
        <Row gutter={[10, 10]} align="middle" justify="space-between">
          <Col>
            <Space wrap>
              <Text type="secondary">{t("Auto-renew")}</Text>
              <Switch
                checked={autoRenew}
                loading={autoRenewSaving}
                disabled={!sub}
                onChange={onToggleAutoRenew}
              />
              <Button
                danger
                icon={<StopOutlined />}
                onClick={onCancel}
                disabled={!sub || autoRenewSaving}
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Col>

          <Col>
            <Space wrap>
              <Button icon={<SyncOutlined />} onClick={load} disabled={!canRun}>
                {t("Refresh")}
              </Button>
              <Button type="primary" onClick={openPlans} disabled={!canRun}>
                {t("Change Plan")}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Credentials (compact) */}
      <Card
        bordered
        style={{ marginBottom: 14, borderRadius: 14 }}
        bodyStyle={{ padding: screens.xs ? 12 : 14 }}
        title={
          <Space>
            <SafetyCertificateOutlined />
            <Text strong>{t("Connect")}</Text>
          </Space>
        }
        extra={
          <Space.Compact>
            <Button onClick={() => form.resetFields()}>{t("Clear")}</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {t("Load")}
            </Button>
          </Space.Compact>
        }
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{ storeId: creds.storeId, email: creds.email, password: "" }}
          onFinish={onSubmitCreds}
        >
          <Row gutter={[12, 8]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={t("Store ID")}
                name="storeId"
                rules={[{ required: true, message: t("Store ID is required") }]}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="e.g., 455564"
                  controls={false}
                  min={1}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={10}>
              <Form.Item
                label={t("Email")}
                name="email"
                rules={[{ required: true, type: "email", message: t("Valid email required") }]}
              >
                <Input size="large" placeholder="you@domain.com" />
              </Form.Item>
            </Col>

            <Col xs={24} md={6}>
              <Form.Item label={t("Password")} name="password">
                <Input.Password size="large" placeholder={t("Optional")} />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            style={{ borderRadius: 12 }}
            type="info"
            showIcon
            message={t("We don't store credentials in the browser.")}
          />
        </Form>
      </Card>

      {/* Main */}
      {!canRun ? (
        <Result
          icon={<ExclamationCircleOutlined />}
          title={t("Enter Store ID and Email to view your subscription")}
          extra={
            <Button type="primary" onClick={() => form.submit()}>
              {t("Load Subscription")}
            </Button>
          }
        />
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : !sub ? (
        <Result
          status="404"
          title={t("No subscription found")}
          subTitle={t("Start a subscription to unlock features.")}
          extra={
            <Button type="primary" onClick={openPlans}>
              {t("View Plans")}
            </Button>
          }
        />
      ) : (
        <>
          <Hero
            sub={sub}
            preview={preview}
            active={active}
            daysLeft={daysLeft}
            onOpenPlans={openPlans}
          />

          {/* Suggestions (compact) */}
          <Card
            style={{ marginTop: 14, borderRadius: 14 }}
            title={
              <Space>
                <ThunderboltOutlined /> <Text strong>{t("Suggestions")}</Text>
              </Space>
            }
          >
            {!suggestions?.length ? (
              <Empty description={t("No suggestions right now")} />
            ) : (
              <Row gutter={[12, 12]}>
                {suggestions.slice(0, 6).map((sugg) => (
                  <Col key={sugg.id} xs={24} sm={12} lg={8}>
                    <Card hoverable style={{ borderRadius: 14 }}>
                      <Space direction="vertical" style={{ width: "100%" }} size={8}>
                        <Space style={{ justifyContent: "space-between", width: "100%" }}>
                          <Text strong>{sugg.name}</Text>
                          <Tag color="blue">{money(sugg?.price?.discounted, currency)}</Tag>
                        </Space>

                        <Space wrap>
                          <LimitPill label="Products" value={sugg?.limits?.productLimit} />
                          <LimitPill label="Users" value={sugg?.limits?.userAccessLimit} />
                        </Space>

                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {sugg.reason}
                        </Text>

                        <Button
                          type="primary"
                          icon={<ArrowRightOutlined />}
                          onClick={() => {
                            const { providerPackageId, providerPackageOptionId } = parsePairId(sugg.id);
                            beginCheckout({ providerPackageId, providerPackageOptionId });
                          }}
                        >
                          {t("Upgrade")}
                        </Button>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        </>
      )}

      {/* Plans section (responsive SaaS) */}
      <div style={{ marginTop: 14 }}>
        <Collapse
          activeKey={showPlans ? ["plans"] : []}
          onChange={(keys) => setShowPlans(keys.includes("plans"))}
          items={[
            {
              key: "plans",
              label: (
                <Space>
                  <DollarCircleOutlined />
                  <Text strong>{t("Plans")}</Text>
                </Space>
              ),
              children: (
                <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: screens.xs ? 12 : 14 }}>
                  <Row gutter={[10, 10]} align="middle" justify="space-between">
                    <Col>
                      <Space wrap>
                        <Segmented
                          value={billingView}
                          onChange={setBillingView}
                          options={[
                            { label: t("Monthly"), value: "monthly" },
                            { label: t("Yearly"), value: "yearly" },
                          ]}
                        />
                        <Segmented
                          value={planFilter}
                          onChange={setPlanFilter}
                          options={[
                            { label: t("All"), value: "All" },
                            { label: t("Basic"), value: "Basic" },
                            { label: t("Starter"), value: "Starter" },
                            { label: t("Professional"), value: "Professional" },
                            { label: t("Business"), value: "Business" },
                          ]}
                        />
                        <Input.Search
                          allowClear
                          placeholder={t("Search")}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          style={{ width: screens.xs ? 200 : 260 }}
                        />
                      </Space>
                    </Col>

                    <Col>
                      <Button icon={<SyncOutlined />} loading={packagesLoading} onClick={reloadPlans}>
                        {t("Reload")}
                      </Button>
                    </Col>
                  </Row>

                  <Divider style={{ margin: "12px 0" }} />

                  {packagesLoading ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : !filteredRows.length ? (
                    <Empty description={t("No plans available")} />
                  ) : (
                    <Row gutter={[12, 12]}>
                      {filteredRows.map((row) => (
                        <Col key={row.id} xs={24} sm={12} md={12} lg={8} xl={6}>
                          <PlanCard
                            row={row}
                            billing={billingView}
                            selecting={selecting}
                            onChoose={beginCheckout}
                            currency={currency}
                          />
                        </Col>
                      ))}
                    </Row>
                  )}

                  {billingView === "monthly" && (
                    <Alert
                      style={{ marginTop: 12, borderRadius: 12 }}
                      type="info"
                      showIcon
                      message={t("Monthly pricing will show when provided by the backend.")}
                    />
                  )}
                </Card>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
